/**
 * Outil « Bac à sable Python » (Boîte à outils, 4e → prépa).
 *
 * On écrit (ou on part d'un exemple) un court programme, on l'exécute : la
 * console et les variables à la fin. « Pas à pas » rejoue l'exécution ligne
 * par ligne : la ligne surlignée, ce qui vient de se passer, les tests (vrai
 * en vert, faux en rouge), les variables qui changent, la console.
 * L'interpréteur est celui du moteur (@jeraisonne/reasoner) : sûr (aucun
 * accès au système, budget d'étapes) et EXACT (0.1 + 0.2 vaut 0.3). Exécuter
 * passe par `runProgram` sans trace (20 000 pas) ; le pas à pas, par
 * `simulateProgram` (2 000 pas enregistrés au plus, ce qu'il dit).
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { ChevronLeft, ChevronRight, ChevronsRight, Play, RotateCcw } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { simulateProgram, type SimStep, type SimVar } from '@jeraisonne/reasoner';
import { python } from '@jeraisonne/reasoner/kernel';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { KEYS, clip, examplesFor, explainError, indent, insertAt } from './examples';

/** lignes de console montrées au plus */
const MAX_LINES = 400;

function VarTable({ vars, empty }: { vars: readonly SimVar[]; empty: string }) {
  if (!vars.length) return <p className="text-ink-500 text-sm">{empty}</p>;
  return (
    <table className="mt-1 w-full text-sm">
      <tbody>
        {vars.map((v) => (
          <tr key={v.name} className="border-ink-100 border-t first:border-t-0">
            <th
              scope="row"
              className="text-ink-700 w-1/3 py-1 pr-2 text-left font-mono font-semibold"
            >
              {v.name}
            </th>
            <td
              className={cn(
                'text-ink-900 break-all py-1 font-mono',
                v.changed && 'bg-primary-50 text-primary-700 rounded-md px-1 font-bold',
              )}
              data-changed={v.changed ? '' : undefined}
            >
              {clip(v.value)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Frames({ step, final }: { step: SimStep; final?: boolean }) {
  return (
    <div className="space-y-2">
      {step.frames.map((f) => (
        <div
          key={f.id}
          className="border-ink-100 rounded-2xl border px-3 py-2"
          data-testid="py-frame"
        >
          <p className="text-ink-600 text-sm font-semibold">
            {f.id ? (
              <>
                Appel <code className="font-mono">{f.name}</code>
              </>
            ) : (
              'Variables'
            )}
            {f === step.frames[step.frames.length - 1] && step.ret !== undefined && (
              <span className="text-success-700 ml-2">
                renvoie <code className="font-mono">{clip(step.ret)}</code>
              </span>
            )}
          </p>
          <VarTable
            vars={f.vars}
            empty={final ? 'Aucune variable.' : 'Aucune variable pour l’instant.'}
          />
        </div>
      ))}
    </div>
  );
}

function Console({ lines }: { lines: readonly string[] }) {
  const cut = lines.length > MAX_LINES;
  return (
    <section
      className="border-ink-100 rounded-2xl border px-3 py-2"
      aria-label="Console"
      data-testid="py-console"
    >
      <p className="text-ink-600 text-sm font-semibold">Console (ce qu’affiche print)</p>
      {lines.length ? (
        <pre className="text-ink-900 mt-1 max-h-64 overflow-auto whitespace-pre font-mono text-sm">
          {lines
            .slice(0, MAX_LINES)
            .map((l) => clip(l, 300))
            .join('\n')}
        </pre>
      ) : (
        <p className="text-ink-500 text-sm">Rien d’affiché.</p>
      )}
      {cut && (
        <p className="text-ink-500 mt-1 text-xs">
          {lines.length - MAX_LINES} lignes de plus ne sont pas montrées.
        </p>
      )}
    </section>
  );
}

function Status({ step }: { step: SimStep }) {
  const tone =
    step.kind === 'error'
      ? 'bg-error-50 text-error-700'
      : step.kind === 'cond'
        ? step.cond?.result
          ? 'bg-success-50 text-success-700'
          : 'bg-error-50 text-error-700'
        : 'bg-ink-50 text-ink-800';
  return (
    <p className={cn('rounded-2xl px-3 py-2 text-sm', tone)} data-testid="py-status">
      {step.line > 0 && <span className="font-bold">{frenchSpaces(`Ligne ${step.line} : `)}</span>}
      {frenchSpaces(step.label)}
    </p>
  );
}

/** bouton du pas à pas : reste focalisable quand il ne fait plus rien (aria-disabled) */
function StepButton({
  label,
  off,
  onClick,
  children,
  main = false,
  buttonRef,
}: {
  label?: string;
  off: boolean;
  onClick: () => void;
  children: ReactNode;
  main?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      title={label}
      aria-disabled={off || undefined}
      onClick={() => {
        if (!off) onClick();
      }}
      className={cn(
        main
          ? cn(PRIMARY_BUTTON, 'flex flex-1 items-center justify-center gap-1')
          : 'border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 min-w-11 items-center justify-center rounded-xl border',
        off && 'cursor-default opacity-40',
      )}
    >
      {children}
    </button>
  );
}

interface RunResult {
  ok: boolean;
  error: string | null;
  output: string[];
  vars: SimVar[];
}

/** exécution complète, sans trace (budget de 20 000 pas) */
function runAll(code: string): RunResult {
  const t = python.runProgram(code, { trace: false });
  const vars = Object.entries(t.globals)
    .filter(([, v]) => v.t !== 'func' && v.t !== 'builtin' && v.t !== 'module')
    .map(([name, v]) => ({ name, value: python.formatValue(v, true), changed: false }));
  return {
    ok: t.ok,
    error: t.error
      ? `${t.error.line > 0 ? `Ligne ${t.error.line} : ` : ''}${explainError(t.error.message, code)}`
      : null,
    output: t.output,
    vars,
  };
}

export function PythonSandboxTool({ level }: ToolProps & { level?: string | null }) {
  const rank = Math.min(9, Math.max(3, schoolLevelRank(level) ?? 9));
  const examples = examplesFor(rank);
  const first = examples[examples.length > 2 ? 1 : 0]!;
  const [exId, setExId] = useState(first.id);
  const [code, setCode] = useState(first.code);
  // ce qui a été exécuté (le texte au moment du clic)
  const [ran, setRan] = useState<{ code: string; stepwise: boolean } | null>(null);
  const [idx, setIdx] = useState(0);
  // le programme de l'élève, gardé quand il passe à un exemple
  const [draft, setDraft] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  // dernier curseur connu dans l'éditeur (une touche d'aide peut venir sans focus)
  const sel = useRef<{ start: number; end: number } | null>(null);
  const pendingSel = useRef<{ start: number; end: number } | null>(null);
  const focusEditor = useRef(false);
  const editorId = useId();

  const example = examples.find((e) => e.id === exId);
  const pristine = example?.code === code;
  const result = useMemo(() => (ran && !ran.stepwise ? runAll(ran.code) : null), [ran]);
  const sim = useMemo(() => (ran?.stepwise ? simulateProgram(ran.code) : null), [ran]);
  const last = sim ? sim.steps.length - 1 : 0;
  const step = sim ? sim.steps[Math.min(idx, last)] : undefined;
  const stale = ran !== null && ran.code !== code;
  const lines = (ran?.code ?? code).split('\n');

  // replacer la sélection après une touche d'aide, ou le focus en revenant à l'éditeur
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    if (pendingSel.current) {
      el.focus();
      el.setSelectionRange(pendingSel.current.start, pendingSel.current.end);
      sel.current = pendingSel.current;
      pendingSel.current = null;
    } else if (focusEditor.current) {
      el.focus();
      focusEditor.current = false;
    }
  });

  // en entrant dans le pas à pas, le focus va sur « Ligne suivante »
  useEffect(() => {
    if (ran?.stepwise) nextRef.current?.focus();
  }, [ran]);

  const remember = () => {
    const el = areaRef.current;
    if (el) sel.current = { start: el.selectionStart, end: el.selectionEnd };
  };

  const run = (stepwise: boolean) => {
    setRan({ code, stepwise });
    setIdx(0);
  };

  const insert = (text: string) => {
    const s = sel.current ?? { start: code.length, end: code.length };
    if (text === '    ') {
      const r = indent(code, s.start, s.end);
      pendingSel.current = { start: r.selStart, end: r.selEnd };
      setCode(r.code);
      return;
    }
    const r = insertAt(code, s.start, s.end, text);
    pendingSel.current = { start: r.caret, end: r.caret };
    setCode(r.code);
  };

  const pick = (id: string, newCode: string) => {
    // le programme modifié de l'élève n'est pas perdu
    if (!examples.some((e) => e.code === code)) setDraft(code);
    setExId(id);
    setCode(newCode);
    setRan(null);
    sel.current = null;
  };

  return (
    <div className="space-y-4">
      <div role="group" aria-label="Exemples" className="flex flex-wrap gap-2">
        {examples.map((e) => (
          <button
            key={e.id}
            type="button"
            aria-pressed={e.id === exId && pristine}
            onClick={() => pick(e.id, e.code)}
            className={cn(
              'min-h-11 rounded-xl border px-3 text-sm font-semibold',
              e.id === exId && pristine
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-700 hover:bg-ink-50',
            )}
          >
            {e.label}
          </button>
        ))}
        {draft !== null && draft !== code && (
          <button
            type="button"
            className="border-ink-200 text-ink-700 hover:bg-ink-50 min-h-11 rounded-xl border border-dashed px-3 text-sm font-semibold"
            onClick={() => {
              setCode(draft);
              setDraft(null);
              setRan(null);
            }}
          >
            Reprendre mon programme
          </button>
        )}
      </div>

      {ran?.stepwise && sim && step ? (
        <section className="space-y-3" aria-label="Exécution pas à pas" data-testid="py-stepper">
          <ol
            className="border-ink-100 overflow-x-auto rounded-2xl border py-1 font-mono text-sm"
            data-testid="py-code"
          >
            {lines.map((t, i) => {
              const n = i + 1;
              const current = step.line === n;
              return (
                <li
                  key={n}
                  aria-current={current ? 'step' : undefined}
                  className={cn(
                    'flex whitespace-pre px-2',
                    current &&
                      (step.kind === 'cond'
                        ? step.cond?.result
                          ? 'bg-success-50'
                          : 'bg-error-50'
                        : step.kind === 'error'
                          ? 'bg-error-50'
                          : 'bg-primary-50'),
                  )}
                >
                  <span
                    aria-hidden
                    className="text-ink-400 w-7 shrink-0 select-none pr-2 text-right"
                  >
                    {n}
                  </span>
                  <code className="text-ink-900">{t || ' '}</code>
                </li>
              );
            })}
          </ol>
          <div aria-live="polite">
            <Status step={step} />
          </div>
          <p className="text-ink-500 text-xs" data-testid="py-counter">
            Pas {Math.min(idx, last) + 1} / {sim.steps.length}
          </p>
          <div className="flex items-center gap-2">
            <StepButton label="Recommencer" off={idx === 0} onClick={() => setIdx(0)}>
              <RotateCcw className="h-5 w-5" aria-hidden />
            </StepButton>
            <StepButton
              label="Reculer d’un pas"
              off={idx === 0}
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden />
            </StepButton>
            <StepButton
              main
              buttonRef={nextRef}
              off={idx >= last}
              onClick={() => setIdx((i) => Math.min(last, i + 1))}
            >
              Ligne suivante
              <ChevronRight className="h-5 w-5" aria-hidden />
            </StepButton>
            <StepButton label="Aller jusqu’à la fin" off={idx >= last} onClick={() => setIdx(last)}>
              <ChevronsRight className="h-5 w-5" aria-hidden />
            </StepButton>
          </div>
          {sim.truncated && idx >= last && (
            <Note tone="error">
              {frenchSpaces(
                `Le pas à pas s’arrête après ${sim.steps.length} pas : le programme est trop long, ou il ne s’arrête pas. « Exécuter » va plus loin.`,
              )}
            </Note>
          )}
          <Frames step={step} final={idx >= last} />
          <Console lines={step.output} />
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => {
              focusEditor.current = true;
              setRan(null);
            }}
          >
            Modifier le programme
          </button>
        </section>
      ) : (
        <>
          <div className="space-y-2">
            <label htmlFor={editorId} className="text-ink-600 text-sm font-semibold">
              Programme Python
            </label>
            <textarea
              id={editorId}
              ref={areaRef}
              value={code}
              maxLength={python.MAX_SOURCE_LENGTH}
              onChange={(e) => {
                setCode(e.target.value);
                sel.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
              }}
              onSelect={remember}
              onKeyUp={remember}
              onClick={remember}
              onBlur={remember}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              wrap="off"
              rows={Math.min(14, Math.max(6, code.split('\n').length + 1))}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 block w-full resize-y overflow-x-auto rounded-2xl border bg-transparent p-3 font-mono text-sm leading-6 outline-none focus:ring-2"
            />
            <div role="group" aria-label="Touches d’aide" className="flex flex-wrap gap-1.5">
              {KEYS.map((k) => (
                <button
                  key={k.label}
                  type="button"
                  aria-label={k.aria}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insert(k.text)}
                  className="border-ink-200 text-ink-700 hover:bg-ink-50 min-h-11 min-w-11 rounded-xl border px-2 font-mono text-sm font-semibold"
                >
                  {k.label}
                </button>
              ))}
            </div>
            <p className="text-ink-500 text-xs">
              {frenchSpaces(
                'Python simplifié : calcul exact (0.1 + 0.2 vaut 0.3), sans input, sans f-string, sans le module math.',
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={cn(PRIMARY_BUTTON, 'flex items-center gap-1')}
              onClick={() => run(false)}
            >
              <Play className="h-4 w-4" aria-hidden />
              Exécuter
            </button>
            <button type="button" className={SECONDARY_BUTTON} onClick={() => run(true)}>
              Pas à pas
            </button>
          </div>
          {result && (
            <div className="space-y-2" data-testid="py-result">
              {stale ? (
                <Note>
                  {frenchSpaces(
                    'Résultat du programme d’avant : exécute à nouveau pour voir tes changements.',
                  )}
                </Note>
              ) : result.ok ? (
                <Note tone="success">Le programme est fini.</Note>
              ) : (
                <Note tone="error">{frenchSpaces(result.error ?? 'Erreur.')}</Note>
              )}
              <Console lines={result.output} />
              <div className="border-ink-100 rounded-2xl border px-3 py-2" data-testid="py-frame">
                <p className="text-ink-600 text-sm font-semibold">Variables à la fin</p>
                <VarTable vars={result.vars} empty="Aucune variable." />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
